import L from "leaflet";

export default class LocationMapComponent {
  constructor({
    mapElementId,
    staticLatitude,
    staticLongitude,
    inputSelectors = {},
    coordinatesFieldId,
    countryMap = {},
    prefectureMap = {}
  }) {
    this.mapElementId = mapElementId;
    this.staticLatitude = staticLatitude;
    this.staticLongitude = staticLongitude;
    this.inputSelectors = inputSelectors;
    this.coordinatesFieldId = coordinatesFieldId;
    this.countryMap = countryMap;
    this.prefectureMap = prefectureMap;
    this.map = null;
    this.marker = null;

    this.init();
  }

  async init() {
    this.suppressGeocode = false;

    const el = document.getElementById(this.mapElementId);
    if (!el) return;

    // Clear previous Leaflet instance if exists
    if (el._leaflet_id) {
      el._leaflet_id = null;
      el.innerHTML = "";
    }

    // Remove old map instance
    if (this.map) {
      this.map.remove();
      this.map = null;
    }

    // Initialize Leaflet map
    this.map = L.map(this.mapElementId).setView([35.6895, 139.6917], 10);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors"
    }).addTo(this.map);

    // Ensure correct sizing
    setTimeout(() => {
      this.map.invalidateSize();
    }, 500);

    // VIEW mode → place static marker if coordinates exist
    if (this.staticLatitude && this.staticLongitude) {
      this.setMarker(this.staticLatitude, this.staticLongitude);
    }

    // EDIT mode → listen to input changes
    if (this.inputSelectors && Object.keys(this.inputSelectors).length > 0) {
      this.bindInputChanges();
    }
  }

  bindInputChanges() {
    Object.values(this.inputSelectors).forEach(selector => {
      const field = document.querySelector(selector);
      if (!field) return;
      if (field.tagName === "SELECT") {
        // Only call when a different option is selected
        let lastValue = field.value;

        // Currently the country / prefecture selects use Select2, so we use Select2 events
        const $field = $(field);
        $field.off('select2:select select2:clear').on('select2:select select2:clear', () => {
          if (this.suppressGeocode) return;
          const newValue = field.value;
          if (newValue !== lastValue) {
            lastValue = newValue;
            this.geocodeAndUpdate();
          }
        });

      } else {
        // Call geocode when the field loses focus
        if (this.suppressGeocode) return;
        field.addEventListener("blur", () => {
          this.geocodeAndUpdate();
        });
      }
    });
  }

  async geocodeAndUpdate(params = null) {
    const fullAddress = this.getFullAddress(params);
    if (!fullAddress.trim()) return;

    this.showLoading(true);

    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}`;

    try {

      const response = await fetch(url, { headers: { "User-Agent": "rails-app" } });
      const data = await response.json();

      // If no results found, show warning message
      if (!data.length) {
        // Create and show warning message below the map
        this.showWarningMessage(I18n.t("backend.work_locations.index.messages.coordinate_not_found", { address: fullAddress }));

        this.showLoading(false);
        return;
      }

      const lat = parseFloat(data[0].lat);
      const lon = parseFloat(data[0].lon);

      this.setMarker(lat, lon);

      if (this.coordinatesFieldId) {
        const hidden = document.getElementById(this.coordinatesFieldId);
        if (hidden) hidden.value = `${lat},${lon}`;
      }
    } catch (e) {
      console.error("Geocode error:", e);
      this.showWarningMessage(I18n.t("backend.work_locations.index.messages.fail_to_geocode"));
    } finally {
      this.showLoading(false);
    }
  }

  // Show warning message
  showWarningMessage(message) {
    // Remove existing warning if any
    const existingWarning = document.querySelector("#geocode-warning");
    if (existingWarning) {
      existingWarning.remove();
    }

    const warningDiv = document.createElement("div");
    const mapEl = document.getElementById("map-container");
    warningDiv.id = "geocode-warning";
    warningDiv.className = "text-danger mt1";
    warningDiv.innerText = message;
    mapEl.parentNode.insertBefore(warningDiv, mapEl);
  }

  // Show / hide loading indicator
  showLoading(isLoading) {
    let loader = document.getElementById(`${this.mapElementId}-loader`);
    if (!loader) {
      loader = document.createElement("div");
      loader.id = `${this.mapElementId}-loader`;
      loader.style.position = "absolute";
      loader.style.top = "50%";
      loader.style.left = "50%";
      loader.style.transform = "translate(-50%, -50%)";
      loader.style.background = "rgba(255,255,255,0.8)";
      loader.style.padding = "10px 20px";
      loader.style.borderRadius = "4px";
      loader.style.boxShadow = "0 0 5px rgba(0,0,0,0.3)";
      loader.style.zIndex = "1000";

      // Add a simple spinner SVG
      loader.innerHTML = `
        <svg width="50" height="50" viewBox="0 0 50 50">
          <circle cx="25" cy="25" r="20" fill="none" stroke="#4C7BE1" stroke-width="5" stroke-linecap="round" stroke-dasharray="31.4 31.4">
            <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite"/>
          </circle>
        </svg>
      `;
      document.getElementById(this.mapElementId).appendChild(loader);
    }

    loader.style.display = isLoading ? "block" : "none";
  }

  getFullAddress(params = null) {
    const parts = [];
    if (params && typeof params === "object") {
      if (params.location) parts.push(params.location);
      if (params.city) parts.push(params.city);
      if (params.region_name) parts.push(params.region_name)
      if (params.zip_code) parts.push(params.zip_code);
      if (params.country_name)  parts.push(params.country_name);
      return parts.join(", ");
    }

    // Address / Street
    const addressEl = document.querySelector(this.inputSelectors.address);
    if (addressEl && addressEl.value) parts.push(addressEl.value);

    // City
    const cityEl = document.querySelector(this.inputSelectors.city);
    if (cityEl && cityEl.value) parts.push(cityEl.value);

    // Prefecture / Region
    const prefectureEl = document.querySelector(this.inputSelectors.prefecture);
    if (prefectureEl && prefectureEl.value) {
      // Get text of the selected option
      const selectedOption = prefectureEl.selectedOptions[0];
      const prefectureName = selectedOption ? selectedOption.text : (this.prefectureMap[prefectureEl.value] || prefectureEl.value);
      parts.push(prefectureName);
    }

    // Zip
    const zipEl = document.querySelector(this.inputSelectors.zip);
    if (zipEl && zipEl.value) parts.push(zipEl.value);

    // Country
    const countryEl = document.querySelector(this.inputSelectors.country_code);
    if (countryEl && countryEl.value) {
      // Get text of the selected option
      const selectedOption = countryEl.selectedOptions[0];
      const countryName = selectedOption ? selectedOption.text : countryEl.value;
      parts.push(countryName);
    }

    return parts.join(", ");
  }

  // Place or move the marker on the map
  // Default to Tokyo if no coordinates provided
  setMarker(lat = 35.682839, lon = 139.759455) {
    // Delete existing warning if any
    const existingWarning = document.querySelector("#geocode-warning");
    if (existingWarning) {
      existingWarning.remove();
    }

    if (!this.map) return;

    if (this.marker) this.map.removeLayer(this.marker);

    this.marker = L.marker([lat, lon], { draggable: true }).addTo(this.map);
    this.map.setView([lat, lon], 15);

    // Optional: update coordinatesFieldId when marker is dragged
    if (this.coordinatesFieldId) {
      this.marker.on("dragend", e => {
        const pos = e.target.getLatLng();
        const hidden = document.getElementById(this.coordinatesFieldId);
        if (hidden) hidden.value = `${pos.lat},${pos.lng}`;
      });
    }
  }
}
